/**
 * Error types enumeration
 */
const ERROR_TYPES = {
    API_ERROR: 'API_ERROR',
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
    AUTHORIZATION_ERROR: 'AUTHORIZATION_ERROR',
    NETWORK_ERROR: 'NETWORK_ERROR',
    TIMEOUT_ERROR: 'TIMEOUT_ERROR',
    FILE_ERROR: 'FILE_ERROR',
    EMAIL_ERROR: 'EMAIL_ERROR',
    OFFICE_ERROR: 'OFFICE_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

/**
 * Error context interface
 */
export interface ErrorContext {
    userId?: string;
    userEmail?: string;
    componentName?: string;
    functionName?: string;
    apiEndpoint?: string;
    requestData?: any;
    responseData?: any;
    additionalInfo?: any;
}

/**
 * Logging options interface
 */
interface LoggingOptions {
    errorMessage?: string;
    errorType?: keyof typeof ERROR_TYPES;
    errorLocation?: string;
    context?: ErrorContext;
    storeInDatabase?: boolean;
    logToConsole?: boolean;
}

export interface ErrorContext {
    userAgent?: string;
    url?: string;
    location?: string;
    customInfo?: any;
    responseData?: any;
}

/**
 * Get error location from stack trace
 * @param error - The error object
 * @returns Error location string
 */
const getErrorLocation = (error: Error): string => {
    if (!error.stack) {
        return 'Unknown location';
    }

    const stackLines = error.stack.split('\n');

    // Skip the first line (error message) and find the first meaningful stack trace
    for (let i = 1; i < stackLines.length; i++) {
        const line = stackLines[i].trim();

        if (line.startsWith('at ') && !line.includes('node_modules')) {
            // Extract file path and line number
            const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);

            if (match) {
                const [, , filePath, lineNumber] = match;
                const fileName = filePath.split('/').pop();

                return `${fileName}:${lineNumber}`;
            }
        }
    }

    return 'Unknown location';
};

/**
 * Determine error type based on error characteristics
 * @param error - The error object
 * @param context - Additional context
 * @returns Error type
 */
const determineErrorType = (error: any): keyof typeof ERROR_TYPES => {
    // Check for specific error types based on error properties
    if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND') {
        return ERROR_TYPES.NETWORK_ERROR;
    }

    if (error.code === 'ETIMEDOUT' || error.message?.includes('timeout')) {
        return ERROR_TYPES.TIMEOUT_ERROR;
    }

    if (error.status === 401 || error.message?.includes('unauthorized')) {
        return ERROR_TYPES.AUTHENTICATION_ERROR;
    }

    if (error.status === 403 || error.message?.includes('forbidden')) {
        return ERROR_TYPES.AUTHORIZATION_ERROR;
    }

    if (error.status >= 400 && error.status < 500) {
        return ERROR_TYPES.VALIDATION_ERROR;
    }

    if (error.status >= 500) {
        return ERROR_TYPES.API_ERROR;
    }

    if (error.message?.includes('email') || error.message?.includes('mail')) {
        return ERROR_TYPES.EMAIL_ERROR;
    }

    return ERROR_TYPES.UNKNOWN_ERROR;
};

/**
 * Format error details for logging
 * @param error - The error object
 * @param context - Additional context information
 * @returns Formatted error object
 */

const formatErrorDetails = (error: any, context: ErrorContext = {}) => {
    const errorDetails = {
        message: error?.message || 'Unknown error occurred',
        stack: error?.stack || null,
        name: error?.name || 'Error',
        code: error?.code || null,
        status: error?.status || error?.statusCode || null,
        timestamp: new Date().toISOString(),

        // Frontend-specific context
        userAgent: context?.userAgent || navigator?.userAgent || null,
        url: context?.url || window?.location?.href || null,
        location: context?.location || window?.location?.pathname || null,

        // Any additional client metadata
        customInfo: context?.customInfo || null,

        // Optional responseData (e.g., for Axios or fetch)
        responseData: context?.responseData || (error?.response?.data ?? null),
    };

    return errorDetails;
};

/**
 * Log error with all required parameters
 * @param error - The error object
 * @param options - Logging options
 * @returns Promise with log result
 */
export const logError = async (error: any, options: LoggingOptions = {}): Promise<any> => {
    const { errorMessage, errorType, errorLocation, context = {}, storeInDatabase = true, logToConsole = true } = options;

    // Format error details
    const formattedDetails = formatErrorDetails(error, context);

    // Determine error type if not provided
    const determinedErrorType = errorType || determineErrorType(error);

    // Get error location if not provided
    const determinedLocation = errorLocation || getErrorLocation(error);

    // Use provided error message or extract from error object
    const finalErrorMessage = errorMessage || formattedDetails.message;

    // Log to console if enabled
    if (logToConsole) {
        console.error(`[${determinedErrorType}] ${finalErrorMessage} at ${determinedLocation}`, formattedDetails);
    }

    // Store in database if enabled
    if (storeInDatabase) {
        if (errorMessage === 'canceled') {
            return;
        }
    }

    return {
        errorMessage: finalErrorMessage,
        errorType: determinedErrorType,
        errorLocation: determinedLocation,
        errorDetails: formattedDetails,
    };
};
